import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import { AXIS } from './chartTheme';
import { tipFormatter, AreaGradient } from './ChartKit';

/**
 * Sección Gastos: gastos operativos del período (tabla operating_expenses),
 * con total + delta, split fijo/variable, desglose por TIPO de gasto
 * (combustible, comida, ...) y por categoría. NO incluye compras a proveedores.
 */

const money = (n: number) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return `$${Math.round(n).toLocaleString('es-AR')}`;
};

const CATEGORY_LABELS: Record<string, string> = {
  estructura: 'Estructura', personal: 'Personal', servicios: 'Servicios',
  impuestos: 'Impuestos', otros: 'Otros', proveedores: 'Proveedores',
  gasto_operativo: 'Gasto operativo', sueldos: 'Sueldos', retiro_dueno: 'Retiro del dueño'
};

function DeltaChip({ value, invert = false }: { value: number | null; invert?: boolean }) {
  if (value == null) return <span className="text-xs text-muted-foreground">sin ref.</span>;
  const good = invert ? value < 0 : value > 0;
  const color = value === 0 ? 'text-muted-foreground bg-muted' : good ? 'text-green-600 bg-green-500/10' : 'text-red-600 bg-red-500/10';
  return <span className={`text-xs px-1.5 py-0.5 rounded ${color}`}>{value > 0 ? '▲ +' : value < 0 ? '▼ ' : ''}{value}% vs per. ant.</span>;
}

export default function GastosSection({ startDate, endDate }: { startDate: string; endDate: string }) {
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) return;
    setError('');
    setLoading(true);
    axios.get('/api/reports/tablero/gastos', { params: { startDate, endDate } })
      .then((r) => setD(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Error cargando gastos'))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading && !d) return <div className="py-10 text-center text-muted-foreground">Cargando gastos...</div>;
  if (error) return <div className="py-10 text-center text-red-600">{error}</div>;
  if (!d) return null;

  const maxTipo = Math.max(1, ...d.porTipo.map((x: any) => x.monto));
  const maxCat = Math.max(1, ...d.porCategoria.map((x: any) => x.monto));

  return (
    <div className={`space-y-4 transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Gastos del período</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{money(d.kpis.total.value)}</p>
          <DeltaChip value={d.kpis.total.deltaPct} invert />
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Movimientos</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.cantidad.value}</p>
          <span className="text-xs text-muted-foreground">gastos cargados</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Fijos</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{money(d.kpis.fijo.value)}</p>
          <span className="text-xs text-muted-foreground">de estructura</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Variables</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{money(d.kpis.variable.value)}</p>
          <span className="text-xs text-muted-foreground">del período</span>
        </CardContent></Card>
      </div>

      {!d.gastosCargados && (
        <p className="text-xs text-amber-600 px-1">
          ⚠ Todavía casi no se cargan gastos. Se cargan desde <span className="underline font-medium">Caja → Registrar Egreso</span> o <span className="underline font-medium">Finanzas → Gastos operativos</span>, eligiendo el tipo de gasto (Combustible, Comida, …).
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Gastos por tipo</h3>
            {d.porTipo.length === 0 && <p className="text-sm text-muted-foreground">Sin gastos en el período</p>}
            <div className="space-y-2">
              {d.porTipo.map((t: any) => (
                <div key={t.tipo}>
                  <div className="flex items-baseline justify-between text-sm mb-0.5">
                    <span className="truncate max-w-[220px]">{t.tipo} <span className="text-xs text-muted-foreground">({t.cantidad})</span></span>
                    <span className="font-medium">{money(t.monto)}</span>
                  </div>
                  <div className="w-full h-1.5 rounded bg-muted overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${(t.monto / maxTipo) * 100}%`, background: '#D85A30' }} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-1">Gastos por mes</h3>
            <p className="text-xs text-muted-foreground mb-3">Histórico de gastos operativos</p>
            <ChartContainer
              config={{ total: { label: 'Gastos', color: 'hsl(var(--chart-1))' } }}
              className="h-[210px] w-full"
            >
              <AreaChart data={d.serie} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                <defs>
                  <AreaGradient id="fillGastos" color="var(--color-total)" />
                </defs>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
                <XAxis dataKey="label" {...AXIS} />
                <YAxis {...AXIS} tickFormatter={(v: number) => money(v)} width={56} />
                <ChartTooltip content={<ChartTooltipContent formatter={tipFormatter({ total: 'Gastos' }, money)} />} />
                <Area type="monotone" dataKey="total" stroke="var(--color-total)" strokeWidth={2.2} fill="url(#fillGastos)" dot={{ r: 3 }} />
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-5">
          <h3 className="font-semibold text-sm mb-3">Gastos por categoría</h3>
          {d.porCategoria.length === 0 && <p className="text-sm text-muted-foreground">Sin gastos en el período</p>}
          <div className="space-y-2">
            {d.porCategoria.map((c: any) => (
              <div key={c.categoria}>
                <div className="flex items-baseline justify-between text-sm mb-0.5">
                  <span className="truncate max-w-[220px]">{CATEGORY_LABELS[c.categoria] || c.categoria} <span className="text-xs text-muted-foreground">({c.cantidad})</span></span>
                  <span className="font-medium">{money(c.monto)}</span>
                </div>
                <div className="w-full h-1.5 rounded bg-muted overflow-hidden">
                  <div className="h-full rounded" style={{ width: `${(c.monto / maxCat) * 100}%`, background: '#6366f1' }} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
